import { AnyPlan, DigitalEntry, DigitalRow } from './types';
import { formatNumberWithSpaces } from '../../common/BudgetTable/utils/formatNumberWithSpaces';

const toSubunits = (v: unknown) => Math.round((Number(v) || 0) * 100);
const fromSubunits = (cents: number) => cents / 100;

export const getCountryData = (plan: AnyPlan, country: string) =>
  plan.countrySetup?.[country];

export const formatBudget = (v?: number, currency = 'PLN'): string =>
  v == null ? 'N/A' : `${formatNumberWithSpaces(v.toFixed(0))} ${currency}`;

export const getPriorityLabel = (entry?: DigitalEntry): string => {
  if (!entry) return 'N/A';
  if (entry.isMandatory) return 'Must have';
  return 'Optional';
};

const calcDigitalBudget = (list: any[] = []): number =>
  fromSubunits(list.reduce((sum, e) => sum + toSubunits(e?.budget?.value), 0));

const calcDigitalTech = (list: any[] = []): number =>
  fromSubunits(
    list.reduce(
      (sum, e) =>
        sum + toSubunits(e?.techCost?.dsp) + toSubunits(e?.techCost?.adServer),
      0
    )
  );

const calcOfflineBudget = (list: any[] = []): number =>
  fromSubunits(
    list
      .filter((e) => e.touchpoint !== 'tech cost')
      .reduce((sum, e) => sum + toSubunits(e?.budget?.value), 0)
  );

const calcOfflineTech = (list: any[] = []): number =>
  fromSubunits(
    toSubunits(list.find((e) => e.touchpoint === 'tech cost')?.budget?.value)
  );

export const getCountryTotals = (
  plan: AnyPlan,
  country: string
): {
  totalBudget: number;
  techCosts: number;
  oneReach?: number;
  currency?: string;
} => {
  const c = getCountryData(plan, country);
  const digital = c?.calculatedBudget ?? [];
  const offline = c?.offline?.tv?.offlineBudget ?? [];

  let currency: string | undefined;

  digital.forEach((e: any) => {
    if (!currency && e?.budget?.currency) currency = e.budget.currency;
  });

  offline.forEach((e: any) => {
    if (!currency && e?.budget?.currency) currency = e.budget.currency;
  });

  return {
    totalBudget: calcDigitalBudget(digital) + calcOfflineBudget(offline),
    techCosts: calcDigitalTech(digital) + calcOfflineTech(offline),
    oneReach: c?.oneReach as number | undefined,
    currency,
  };
};

export const getGlobalTotals = (
  plan: AnyPlan
): {
  totalBudget: number;
  techCosts: number;
} => {
  let totalBudget = 0;
  let techCosts = 0;

  Object.keys(plan.countrySetup ?? {}).forEach((country) => {
    const c = getCountryData(plan, country);
    const digital = c?.calculatedBudget ?? [];
    const offline = c?.offline?.tv?.offlineBudget ?? [];

    totalBudget += calcDigitalBudget(digital) + calcOfflineBudget(offline);
    techCosts += calcDigitalTech(digital) + calcOfflineTech(offline);
  });

  return { totalBudget, techCosts };
};

export const getDigitalRowsForCountry = (
  plans: AnyPlan[],
  country: string
): DigitalRow[] => {
  const map = new Map<string, DigitalRow>();

  plans.forEach((plan) => {
    const digital: DigitalEntry[] =
      getCountryData(plan, country)?.calculatedBudget ?? [];

    digital.forEach((entry) => {
      const key = `${entry.touchpointName}|${entry.touchpointFunnel}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          touchpointName: entry.touchpointName,
          touchpointFunnel: entry.touchpointFunnel,
        });
      }
    });
  });

  return Array.from(map.values());
};

export const getDigitalEntry = (
  plan: AnyPlan,
  country: string,
  row: DigitalRow
): DigitalEntry | undefined => {
  const list: DigitalEntry[] =
    getCountryData(plan, country)?.calculatedBudget ?? [];

  return list.find(
    (e) =>
      e.touchpointName === row.touchpointName &&
      e.touchpointFunnel === row.touchpointFunnel
  );
};
